import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getFeatureToggles } from 'zite-endpoints-sdk';

interface FeatureToggleCtx {
  /** Is this feature key visible to the current user? Checks global toggle + group + overrides */
  isEnabled: (key: string) => boolean;
  /** Is this route path enabled? Maps route → feature key first */
  isRouteEnabled: (route: string) => boolean;
  /** All enabled keys for the current user */
  enabledKeys: Set<string>;
  /** All toggle states (admin view) */
  allToggles: { key: string; enabled: boolean }[];
  /** Is current user admin? */
  isAdmin: boolean;
  /** Change log entries */
  changeLog: { id: string; action: string; actorEmail: string; target: string; timestamp: string; details: string }[];
  /** Reload from server */
  reload: () => void;
  loading: boolean;
}

const Ctx = createContext<FeatureToggleCtx>({
  isEnabled: () => true,
  isRouteEnabled: () => true,
  enabledKeys: new Set(),
  allToggles: [],
  isAdmin: false,
  changeLog: [],
  reload: () => {},
  loading: true,
});

export function useFeatureToggles() {
  return useContext(Ctx);
}

// Route → feature key map (built from featureRegistry but kept lightweight for frontend)
const ROUTE_KEY_MAP: Record<string, string> = {
  '/': 'home', '/actions': 'actions', '/notifications': 'notifications', '/my-tasks': 'my-tasks',
  '/modules': 'modules', '/compliance': 'compliance', '/compliance-certs': 'compliance-certs',
  '/handbook': 'handbook', '/sops': 'sops', '/ask': 'ask', '/quiz-history': 'quiz-history', '/import-report': 'import-report',
  '/profile': 'profile', '/documents': 'documents', '/shared-documents': 'shared-documents',
  '/document-library': 'document-library', '/policies': 'policies', '/my-shifts': 'my-shifts',
  '/leave': 'leave', '/pay-leave': 'pay-leave', '/payslips': 'payslips',
  '/availability': 'availability', '/uniform': 'uniform', '/my-keys': 'my-keys',
  '/training-requests': 'training-requests', '/reviews': 'reviews', '/journal': 'journal',
  '/certificates': 'certificates', '/my-rewards': 'my-rewards', '/recognition': 'recognition',
  '/benefits': 'benefits', '/employee-of-month': 'employee-of-month', '/feedback': 'feedback',
  '/suggestions': 'suggestions', '/info-hub': 'info-hub', '/onboarding': 'onboarding',
  '/journey': 'journey', '/customer-reviews': 'customer-reviews',
  '/pub-info': 'pub-info', '/floor-plans': 'floor-plans', '/health-safety': 'health-safety',
  '/tools': 'tools', '/gp-calculator': 'gp-calculator', '/shift-prep': 'shift-prep',
  '/event-setup': 'event-setup', '/checklists': 'checklists', '/my-tools': 'my-tools',
  '/manager': 'manager', '/manager-ops': 'manager-ops', '/tasks': 'tasks',
  '/team-readiness': 'team-readiness', '/training-analytics': 'training-analytics',
  '/training-renewals': 'training-renewals', '/compliance-expiry': 'compliance-expiry',
  '/data-quality': 'data-quality', '/fire-log': 'fire-log', '/coshh': 'coshh',
  '/maintenance': 'maintenance', '/reports': 'reports', '/engagement': 'engagement',
  '/daily-ops-bridge': 'daily-ops-bridge',
  '/refresher-schedule': 'refresher-schedule',
  '/train': 'interactive-training',
  '/admin': 'admin', '/app-settings': 'app-settings', '/training-builder': 'training-builder',
  '/onboarding-journeys': 'onboarding-journeys', '/audit-readiness': 'audit-readiness',
  '/permission-matrix': 'permission-matrix', '/email-safety': 'email-safety',
  '/document-access-log': 'document-access-log', '/bridge-health': 'bridge-health',
  '/data-backup': 'data-backup', '/system-docs': 'system-docs', '/handover-docs': 'handover-docs',
  '/dev-board': 'dev-board', '/feature-audit': 'feature-audit',
};

export default function FeatureToggleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [enabledKeys, setEnabledKeys] = useState<Set<string>>(new Set());
  const [allToggles, setAllToggles] = useState<{ key: string; enabled: boolean }[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [changeLog, setChangeLog] = useState<FeatureToggleCtx['changeLog']>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    if (!user) return;
    setLoading(true);
    getFeatureToggles({})
      .then(data => {
        setEnabledKeys(new Set(data.userEnabledKeys));
        setAllToggles(data.toggles);
        setIsAdmin(data.isAdmin);
        setChangeLog(data.changeLog);
      })
      .catch(() => {
        // Fallback: all features enabled
        setEnabledKeys(new Set(Object.values(ROUTE_KEY_MAP)));
      })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const isEnabled = useCallback((key: string) => {
    if (loading) return true; // default to visible while loading
    return enabledKeys.has(key);
  }, [enabledKeys, loading]);

  const isRouteEnabled = useCallback((route: string) => {
    const key = ROUTE_KEY_MAP[route];
    if (!key) return true; // unknown routes stay visible
    return isEnabled(key);
  }, [isEnabled]);

  return (
    <Ctx.Provider value={{ isEnabled, isRouteEnabled, enabledKeys, allToggles, isAdmin, changeLog, reload, loading }}>
      {children}
    </Ctx.Provider>
  );
}
